import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { useAuth } from '../hooks/use-auth'
import { useAvatarSrc } from '../hooks/use-avatar-src'
import { get, post } from '../helper/network'
import API from '../helper/api'
import { ArrowLeft, Camera, Loader2 } from 'lucide-react'
import { Logo } from '../components/ui/Logo'
import { useI18n } from '../i18n'
import axios from 'axios'
import { CurrentPlanCard } from './pricing/CurrentPlanCard'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, isLoggedIn, refreshUserInfo } = useAuth()
  const avatarSrc = useAvatarSrc(user?.avatarUrl)
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)

  const [userInfo, setUserInfo] = useState<any>(null)

  const [form, setForm] = useState({
    nickName: '',
    avatarUrl: '',
    phone: '',
    email: '',
    gender: '',
    department: '',
    position: '',
    bio: '',
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login')
      return
    }
    loadUserInfo()
  }, [isLoggedIn])

  const loadUserInfo = async () => {
    try {
      const res: any = await get(API.GET_USER_INFO, { username: user?.username })
      if (res && res.code === 200 && res.data) {
        const d = res.data
        setUserInfo(d)
        setForm({
          nickName: d.nickName || '',
          avatarUrl: d.avatarUrl || '',
          phone: d.phoneNumber || '',
          email: d.email || '',
          gender: d.gender || '',
          department: d.department || '',
          position: d.position || '',
          bio: d.bio || '',
        })
      }
    } catch {}
  }

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setSaved(false)
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setError(t('文件大小不能超过5MB', 'File size must be under 5MB'))
      return
    }

    setUploading(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('file', file)

      const res: any = await axios.post(API.GET_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        validateStatus: (status: number) => status >= 200 && status < 400,
      })

      if (res.data && res.data.code === 200 && res.data.data) {
        const data = res.data.data
        const avatarUrl = data.url || data.file_url || (data.id ? `/file/upload/${data.id}` : '')

        if (avatarUrl) {
          const updateRes: any = await post(API.POST_UPDATE_PROFILE, { avatarUrl })
          if (updateRes && updateRes.code === 200) {
            setForm((prev) => ({ ...prev, avatarUrl }))
            setSaved(true)
            refreshUserInfo()
          } else {
            setError(t('头像保存失败', 'Failed to save avatar'))
          }
        } else {
          setError(t('上传成功但未获取到图片地址', 'Upload succeeded but no URL returned'))
        }
      } else {
        setError(t('头像上传失败', 'Avatar upload failed'))
      }
    } catch {
      setError(t('头像上传失败', 'Avatar upload failed'))
    }

    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    setSaved(false)

    try {
      const res: any = await post(API.POST_UPDATE_PROFILE, {
        nickName: form.nickName || undefined,
        avatarUrl: form.avatarUrl || undefined,
        email: form.email || undefined,
        gender: form.gender || undefined,
        department: form.department || undefined,
        position: form.position || undefined,
        bio: form.bio || undefined,
      })
      if (res && res.code === 200) {
        setSaved(true)
        refreshUserInfo()
      } else {
        setError(res?.message || t('保存失败', 'Save failed'))
      }
    } catch {
      setError(t('网络错误', 'Network error'))
    }

    setLoading(false)
  }

  if (!isLoggedIn) return null

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* 顶栏 */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-2 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
          <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
          {t('返回工作台', 'Back')}
        </Link>
        <div className="ml-auto" />
      </header>

      <div className="flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">

        <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-white/80 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
          <div className="text-center mb-6">
            <h1 className="text-lg font-semibold text-[#1a1a1a]">{t('个人信息', 'Profile')}</h1>
            <p className="text-xs text-gray-400 mt-1">{t('编辑你的账号信息', 'Edit your account info')}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">{error}</div>
          )}
          {saved && (
            <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs">
              {t('保存成功', 'Saved successfully')}
            </div>
          )}

          {/* 头像上传 */}
          <div className="flex justify-center mb-6">
            <div className="relative group cursor-pointer" onClick={handleAvatarClick}>
              <Avatar className="h-20 w-20 ring-2 ring-white shadow-md">
                {avatarSrc && <AvatarImage src={avatarSrc} alt={form.nickName} />}
                <AvatarFallback className="bg-orange-500 text-white text-2xl">
                  {form.nickName?.[0] || user?.username?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">{t('昵称', 'Nickname')}</Label>
              <Input
                value={form.nickName}
                onChange={(e) => handleChange('nickName', e.target.value)}
                placeholder={t('请输入昵称', 'Enter nickname')}
                maxLength={50}
                className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">{t('手机号', 'Phone')}</Label>
              <div className="h-10 px-4 rounded-xl bg-gray-50 border border-black/[0.04] text-[#1a1a1a] text-sm flex items-center">
                {form.phone || '-'}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">{t('邮箱', 'Email')}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={t('请输入邮箱', 'Enter email')}
                className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">{t('部门', 'Department')}</Label>
                <Input
                  value={form.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder={t('部门', 'Dept')}
                  maxLength={100}
                  className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">{t('职位', 'Position')}</Label>
                <Input
                  value={form.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  placeholder={t('职位', 'Position')}
                  maxLength={100}
                  className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">{t('个人简介', 'Bio')}</Label>
              <Input
                value={form.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                placeholder={t('简单介绍一下自己', 'Tell us about yourself')}
                maxLength={500}
                className="bg-white/60 border-black/[0.06] text-[#1a1a1a] placeholder:text-gray-300 focus:border-orange-200 rounded-xl h-10 text-sm"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] h-10 text-sm font-medium rounded-xl"
              disabled={loading}
            >
              {loading ? t('保存中...', 'Saving...') : t('保存修改', 'Save Changes')}
            </Button>
          </form>

          {/* 当前套餐 */}
          <div className="mt-6 pt-6 border-t border-black/[0.04]">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
              {t('当前套餐', 'Current Plan')}
            </h3>
            <CurrentPlanCard />
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}
